import { LIST_URL_SPECS } from '@pmp/utils';

import type { AppliedCrudClone, CloneController, CrudCloneInstruction, ReadCloneController } from './cloneActions';
import {
  applyCrudClone,
  describeCloneApplied,
  describeCloneOffer,
  holdCrudClone,
  offerOpenCloneCall,
  takeOpenCloneOffer,
  OPEN_CLONE_PARAMETERS,
  OPEN_CLONE_TABLE,
} from './cloneActions';
import { assistantNavigate } from './listRegistry';
import type { AssistantAction } from '@ohpen/enso-ui';

import { isSafePath, pathname } from './proposalChannel';

export { clearPendingCrudClone, drainCrudClone, holdCrudClone, takeCrudClone } from './cloneActions';

// Which lists can be cloned from right now, so the agent's proposal reaches the rows the user is pointing at.
//
// Not a store, for the same reason the list and draft controllers are not one: nothing renders from this, and
// the transport reads it outside React. Each list registers itself while it is mounted and clears it on the
// way out, and a proposal for a list that is not mounted waits in `cloneActions` until it comes up.

/**
 * What became of a proposal. A list that is not open is told apart from one that is open and cannot carry the
 * change, because the panel says them to the user in different words.
 */
export type CrudCloneOutcome =
  | { status: 'opened'; applied: AppliedCrudClone }
  | { status: 'navigating' }
  | { status: 'needs-confirmation' }
  | { status: 'refused'; reason: string }
  | { status: 'not-open' };

const controllers = new Map<string, ReadCloneController>();

export const registerCloneController = (entityType: string, read: ReadCloneController): void => {
  controllers.set(entityType, read);
};

export const unregisterCloneController = (entityType: string, read: ReadCloneController): void => {
  // Only when it is still this list's own reader: on a route change the next list can register before the
  // previous one unmounts, and clearing then would leave the app with no reachable rows.
  if (controllers.get(entityType) === read) controllers.delete(entityType);
};

export const findCloneController = (entityType: string): CloneController | undefined => controllers.get(entityType)?.() ?? undefined;

// The path the agent sent, only when it is this app's own path for the list the proposal names. The registry,
// not the proposal, decides where a list lives.
const navigablePath = (instruction: CrudCloneInstruction): string | null => {
  const spec = LIST_URL_SPECS[instruction.entity];
  const path = instruction.path;
  if (spec === undefined || path === undefined || !isSafePath(path)) return null;

  return pathname(path) === spec.route ? path : null;
};

/**
 * Opens the clone the proposal describes: straight away when its list is on screen, and otherwise by
 * navigating there and letting the list open it as it arrives.
 *
 * A proposal that names no rows is never carried across a navigation, whatever it says: it means "the rows
 * the user has selected", and on a list they have not opened there are none. It is refused here rather than
 * arriving as an empty clone.
 *
 * The navigation half only ever runs for a proposal the user has agreed to. Nothing is submitted either way,
 * but taking someone off the page they are working on is not a side effect of answering their question.
 */
export const runCrudClone = (instruction: CrudCloneInstruction): CrudCloneOutcome => {
  const controller = findCloneController(instruction.entity);
  if (controller !== undefined) {
    const outcome = applyCrudClone(instruction, controller);
    return 'applied' in outcome ? { status: 'opened', applied: outcome.applied } : { status: 'refused', reason: outcome.refused };
  }

  const path = navigablePath(instruction);
  if (path === null) return { status: 'not-open' };
  if ((instruction.rows ?? []).length === 0)
    return { status: 'refused', reason: `${instruction.list ?? instruction.entity} is not open, so there is nothing selected to clone` };
  if (instruction.openClone !== true) return { status: 'needs-confirmation' };

  holdCrudClone(instruction);
  if (!assistantNavigate(path)) return { status: 'not-open' };

  return { status: 'navigating' };
};

/**
 * The action behind the panel's confirm affordance: the user agreeing to be taken to a list they do not have
 * open, with the clone opened on it. Registered on the assistant config, so enso offers it on the step itself
 * and runs it when they click.
 *
 * `confirm` is enso's default, and is set here anyway because the whole point of this action is that it is
 * never taken without being agreed to.
 */
export const openCloneTableAction: AssistantAction = {
  name: OPEN_CLONE_TABLE,
  description:
    'Open one of the portal lists with a clone of the rows the answer describes, carrying the changes it proposes. Offered to the user, never taken on its own. It submits nothing.',
  parameters: OPEN_CLONE_PARAMETERS,
  confirm: 'confirm',
  execute: params => {
    const instruction = takeOpenCloneOffer(params);
    if (instruction === null) return 'That proposal is no longer available.';

    const list = instruction.list ?? instruction.entity;
    const outcome = runCrudClone(instruction);
    switch (outcome.status) {
      case 'opened':
        return describeCloneApplied(outcome.applied, instruction.list);
      case 'navigating':
        return `Opening ${list}`;
      case 'refused':
        return outcome.reason;
      default:
        return `${list} could not be opened`;
    }
  },
  serializeResult: result => String(result),
};

/** The offer the panel paints when the list is elsewhere: the call, and the words on the step. */
export const offerClone = (instruction: CrudCloneInstruction): { arguments: Record<string, unknown>; summary: string } => ({
  arguments: offerOpenCloneCall(instruction),
  summary: describeCloneOffer(instruction),
});
